import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Truck, Plus, Loader2, Package, Send, PackageCheck, BadgeCheck, Mail, Save } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { NotificationBlock, insertNotificationToken, wrapNotificationSelection } from '../PrestationPlanning/PrestationNotificationsPage';

// Jetons propres au mail consolidé envoyé au client final quand sa démarche mêlait plusieurs
// fournisseurs — {{articles}} regroupe alors les articles de TOUS les fournisseurs concernés.
const MULTI_SUPPLIER_TOKENS = [
    { token: '{{prenom}}', label: 'Prénom du contact' },
    { token: '{{nom}}', label: 'Nom (ou raison sociale) du contact' },
    { token: '{{entreprise}}', label: 'Nom de votre établissement' },
    { token: '{{articles}}', label: 'Articles commandés (tous fournisseurs)' },
    { token: '{{date}}', label: 'Date de la démarche (RDV ou question, si connue)' },
    { token: '{{heure}}', label: 'Heure de la démarche (RDV ou question, si connue)' },
    { token: '{{date_debut}}', label: 'Date de début' },
    { token: '{{heure_debut}}', label: 'Heure de début' },
    { token: '{{date_fin}}', label: 'Date de fin (si connue)' },
    { token: '{{heure_fin}}', label: 'Heure de fin (si connue)' },
    { token: '{{montant_regle}}', label: 'Montant déjà réglé par le client' },
    { token: '{{montant_total}}', label: 'Montant total de la démarche' },
    { token: '{{reste_a_payer}}', label: 'Reste à payer par le client' },
    { token: '{{logo}}', label: 'Votre logo' },
];

function MultiSupplierEmailModal({ onClose }: { onClose: () => void }) {
    const [subject, setSubject] = React.useState('');
    const [body, setBody] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [activeField, setActiveField] = React.useState<'subject' | 'body'>('body');
    const subjectRef = React.useRef<HTMLInputElement>(null);
    const bodyRef = React.useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
        moduleApi.getClientNotificationSettings().then((s: any) => {
            setSubject(s?.multi_supplier_confirmed_subject || '');
            setBody(s?.multi_supplier_confirmed_body || '');
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const insertToken = (token: string) => {
        if (activeField === 'subject') insertNotificationToken(subjectRef.current, subject, setSubject, token);
        else insertNotificationToken(bodyRef.current, body, setBody, token);
    };
    const wrapBody = (marker: string) => wrapNotificationSelection(bodyRef.current, body, setBody, marker);

    const handleSave = async () => {
        setSaving(true);
        try {
            // PUT /client-notification-settings réécrit l'intégralité de la ligne : on relit d'abord
            // l'existant pour ne pas écraser les autres réglages (Facture, Congés, etc.) gérés depuis la
            // page Paramètres > Notifications.
            const current: any = await moduleApi.getClientNotificationSettings().catch(() => ({}));
            await moduleApi.saveClientNotificationSettings({
                ...current,
                multi_supplier_confirmed_subject: subject,
                multi_supplier_confirmed_body: body,
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save multi-supplier email settings', e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open
            onClose={onClose}
            title="Mail multi-fournisseurs"
            icon={<Mail size={16} />}
            maxWidth="max-w-2xl"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Fermer</Button>
                    <Button onClick={handleSave} disabled={saving || loading}>
                        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {saving ? 'Enregistrement...' : saved ? 'Enregistré ✓' : 'Enregistrer'}
                    </Button>
                </>
            }
        >
            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={24} /></div>
            ) : (
                <div className="space-y-2">
                    <p className="text-xs text-content-muted italic">
                        Envoyé au client final dès que son paiement est confirmé, quand sa démarche mêlait des articles de plusieurs fournisseurs — remplace alors le mail individuel de chaque fournisseur (réglable depuis chaque fiche fournisseur pour le cas où un seul fournisseur est concerné).
                    </p>
                    <NotificationBlock title="Mail au client final (plusieurs fournisseurs)"
                        subject={subject} setSubject={setSubject} body={body} setBody={setBody}
                        subjectRef={subjectRef} bodyRef={bodyRef}
                        activeField={activeField} setActiveField={setActiveField}
                        onInsertToken={insertToken} onWrap={wrapBody} tokens={MULTI_SUPPLIER_TOKENS} />
                </div>
            )}
        </Modal>
    );
}

interface SupplierArticle {
    id: string;
    label: string;
    price_ttc: number;
    vat_rate: number;
}

interface Supplier {
    id: string;
    name: string;
    email: string;
    phone: string;
    address: string;
    notes: string;
    logo_url?: string | null;
    articles: SupplierArticle[];
    order_counts?: { pending: number; to_order: number; ordered: number; confirmed: number };
}

function CreateSupplierModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
    const [name, setName] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [address, setAddress] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    const handleSave = async () => {
        if (!name.trim()) { setError('Le nom est requis.'); return; }
        setSaving(true);
        setError('');
        try {
            const res: any = await moduleApi.createSupplier({ name: name.trim(), email: email.trim(), phone: phone.trim(), address: address.trim() });
            onCreated(res.id);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de la création.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open
            onClose={onClose}
            title="Nouveau fournisseur"
            maxWidth="max-w-md"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Annuler</Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {saving ? 'Création...' : 'Créer'}
                    </Button>
                </>
            }
        >
            <div className="space-y-4">
                <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Nom *</label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Grossiste Chocolat SARL" autoFocus />
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Email</label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="commandes@fournisseur.fr" />
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Téléphone</label>
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Adresse</label>
                    <Input value={address} onChange={(e) => setAddress(e.target.value)} />
                </div>
                <p className="text-xs text-content-muted italic">Vous pourrez compléter la fiche (notes, logo, catalogue d'articles) une fois le fournisseur créé.</p>
                {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
            </div>
        </Modal>
    );
}

export const FournisseursModule: React.FC = () => {
    const navigate = useNavigate();
    const [suppliers, setSuppliers] = React.useState<Supplier[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [showCreate, setShowCreate] = React.useState(false);
    const [showMultiSupplierEmail, setShowMultiSupplierEmail] = React.useState(false);

    React.useEffect(() => {
        moduleApi.getSuppliers().then((d: any) => setSuppliers(Array.isArray(d) ? d : [])).catch(() => setSuppliers([])).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="space-y-6">
            <header className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-xl font-bold text-content tracking-tight flex items-center gap-2"><Truck size={18} /> Fournisseurs</h1>
                    <p className="text-content-muted text-sm mt-0.5">Vos fournisseurs et leur catalogue d'articles, utilisables dans vos Démarches client.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="secondary" onClick={() => setShowMultiSupplierEmail(true)}>
                        <Mail size={15} /> Mail multi-fournisseurs
                    </Button>
                    <Button onClick={() => setShowCreate(true)}>
                        <Plus size={15} /> Nouveau fournisseur
                    </Button>
                </div>
            </header>

            {suppliers.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl">
                    <Truck size={28} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Aucun fournisseur pour l'instant.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {suppliers.map((s) => (
                        <motion.div
                            key={s.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-surface rounded-2xl border border-border p-6 hover:border-white/20 transition-all cursor-pointer group relative overflow-hidden"
                            onClick={() => navigate(`/fournisseurs/${s.id}`)}
                        >
                            <div className="relative z-10">
                                <div className="flex justify-between items-start mb-6">
                                    <div className="w-12 h-12 bg-surface-soft rounded-xl flex items-center justify-center border border-border overflow-hidden">
                                        {s.logo_url ? (
                                            <img src={resolveLogoUrl(s.logo_url)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <Truck className="text-content-muted" size={22} />
                                        )}
                                    </div>
                                    <span className="text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest bg-brand-500/10 text-brand-600 dark:text-brand-400 flex items-center gap-1">
                                        <Package size={11} /> {s.articles.length} article{s.articles.length > 1 ? 's' : ''}
                                    </span>
                                </div>
                                <h3 className="text-xl font-bold text-content mb-1 group-hover:text-brand-600 dark:text-brand-400 transition-colors truncate">{s.name}</h3>
                                <p className="text-content-muted text-sm truncate">
                                    {[s.email, s.phone].filter(Boolean).join(' · ') || 'Aucun contact renseigné'}
                                </p>
                                <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 gap-2 text-center">
                                    <div>
                                        <p className="text-sm font-bold text-content flex items-center justify-center gap-1"><Send size={12} className="text-content-muted" /> {s.order_counts?.to_order ?? 0}</p>
                                        <p className="text-[9px] text-content-muted mt-0.5 uppercase tracking-wide">À commander</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-content flex items-center justify-center gap-1"><PackageCheck size={12} className="text-content-muted" /> {s.order_counts?.ordered ?? 0}</p>
                                        <p className="text-[9px] text-content-muted mt-0.5 uppercase tracking-wide">Commandées</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-content flex items-center justify-center gap-1"><BadgeCheck size={12} className="text-content-muted" /> {s.order_counts?.confirmed ?? 0}</p>
                                        <p className="text-[9px] text-content-muted mt-0.5 uppercase tracking-wide">Confirmées</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {showCreate && (
                <CreateSupplierModal
                    onClose={() => setShowCreate(false)}
                    onCreated={(id) => navigate(`/fournisseurs/${id}`)}
                />
            )}

            {showMultiSupplierEmail && (
                <MultiSupplierEmailModal onClose={() => setShowMultiSupplierEmail(false)} />
            )}
        </div>
    );
};
